'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Loader2, AlertCircle, Lock, Mail } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

export default function LoginPage() {
  const router = useRouter();
  const { signIn } = useAuth();

  // Debug: verify signIn is a function
  console.log("signIn:", signIn);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setLoading(true);
    setError('');
    try {
      await signIn(email.trim(), password);
      router.push('/dashboard');
    } catch (err: any) {
      console.error('Login error:', err);
      const msg = err?.message || '';
      if (msg.includes('Email not confirmed')) {
        setError('Din e-mail er ikke bekræftet endnu. Tjek din indbakke for en bekræftelsesmail.');
      } else if (msg.includes('Invalid login credentials') || msg.includes('invalid_credentials')) {
        setError('Forkert e-mail eller adgangskode. Prøv igen.');
      } else if (msg.includes('User not found')) {
        setError('Ingen konto fundet med denne e-mail. Kontakt din administrator.');
      } else {
        setError(msg || 'Noget gik galt. Prøv igen.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div suppressHydrationWarning className="min-h-screen bg-[#141820] flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <div className="w-14 h-14 rounded-2xl overflow-hidden shadow-lg">
            <Image
              src="/assets/images/GembaKonceptNTbig-1773956036188.png"
              alt="Gemba logo"
              width={56}
              height={56}
              className="object-cover w-full h-full"
            />
          </div>
        </div>

        <div className="bg-[#1e2433] rounded-2xl border border-[#2a3040] p-8">
          <div className="mb-6">
            <h1 className="text-xl font-bold text-[#e8dcc8] mb-1">Log ind på Gemba</h1>
            <p className="text-sm text-[#6e7a8a]">Velkommen tilbage. Indtast dine oplysninger for at fortsætte.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-[#c4b89e] mb-1.5">E-mail</label>
              <div className="relative">
                <Mail size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#6e7a8a]" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  placeholder="din@email.dk"
                  autoComplete="email"
                  className="w-full pl-9 pr-3.5 py-2.5 border border-[#2a3040] rounded-xl text-sm bg-[#252b3a] text-[#d4c8b4] placeholder:text-[#4a5060] focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#c4b89e] mb-1.5">Adgangskode</label>
              <div className="relative">
                <Lock size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#6e7a8a]" />
                <input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(''); }}
                  placeholder="Din adgangskode"
                  autoComplete="current-password"
                  className="w-full pl-9 pr-10 py-2.5 border border-[#2a3040] rounded-xl text-sm bg-[#252b3a] text-[#d4c8b4] placeholder:text-[#4a5060] focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6e7a8a] hover:text-[#a89880]"
                >
                  {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-900/20 border border-red-700/40 rounded-lg">
                <AlertCircle size={14} className="text-red-400 flex-shrink-0" />
                <p className="text-xs text-red-400">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !email.trim() || !password}
              className="w-full py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white text-sm font-bold rounded-xl hover:opacity-90 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
            >
              {loading ? (
                <><Loader2 size={16} className="animate-spin" />Logger ind...</>
              ) : (
                'Log ind'
              )}
            </button>
          </form>

          <p className="mt-6 text-center text-xs text-[#6e7a8a]">
            Har du ikke en konto?{' '}
            <a href="/landing" className="text-amber-400 hover:text-amber-300 font-medium transition-colors">
              Anmod om adgang
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
